import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, FormField, Input, Modal, SpaceBetween } from '@amzn/awsui-components-core';

interface SubsectionIdModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSave: (subsectionId: string) => void;
  initialValue?: string;
}

/**
 * Modal for editing a heading's subsection ID attribute.
 * The ID is used as an HTML id attribute so that #fragment links work natively.
 */
const SubsectionIdModal: React.FC<SubsectionIdModalProps> = ({
  visible,
  onDismiss,
  onSave,
  initialValue,
}) => {
  const [value, setValue] = useState(initialValue || '');

  useEffect(() => {
    if (visible) {
      setValue(initialValue || '');
    }
  }, [visible, initialValue]);

  const handleSave = useCallback(() => {
    onSave(value.trim());
  }, [onSave, value]);

  const handleChange = useCallback((event: { detail: { value: string } }) => {
    setValue(event.detail.value);
  }, []);

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Edit Subsection ID"
      data-testid="subsection-id-modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
      closeAriaLabel="Close modal"
    >
      <FormField
        label="Subsection ID"
        description="Set a custom ID for this heading. This enables #fragment anchor links (e.g. #my-section-id) to link directly to this heading."
        stretch
      >
        <Input
          value={value}
          onChange={handleChange}
          placeholder="e.g. my-section-id"
          data-testid="subsection-id-input"
        />
      </FormField>
    </Modal>
  );
};

export default SubsectionIdModal;
